import React, { useState, useRef, useEffect } from "react";
import {
	BrowserMultiFormatReader,
	BarcodeFormat,
	IScannerControls,
} from "@zxing/browser";
import { DecodeHintType } from "@zxing/library";
import { AttachMaterialDialog } from "../../components/Dialog/attach-material-dialog.component";
import { CircularProgress } from "@mui/material";
import { Logger } from "@/lib/logger/Logger";
import { useNavigate } from "react-router-dom";
import gs1AiJson from "./GS1-Application-Identifiers.json";
import { showNotification } from "@/dentlab/src/store/Central/selectors";

interface ApplicationIdentifier {
	ai: string;
	regEx: string;
	description: string;
	code: string;
	type: "string" | "date";
}

interface ReturnedData {
	code: string;
	description: string;
	value: string;
	type: "string" | "date";
}

// Define the structure of the gs1AiJson file
interface GS1JsonData {
	"@context": Record<string, string>;
	applicationIdentifiers: any[];
	"@graph"?: any[]; // Make '@graph' optional since we're checking for its existence
}

const aiDefinitions: ApplicationIdentifier[] = (
	(gs1AiJson as GS1JsonData)["@graph"] ||
	(gs1AiJson as GS1JsonData).applicationIdentifiers
).map((node) => {
	const id: string = node["applicationIdentifier"];
	return {
		ai: id,
		code: node["title"],
		description: node["description"],
		regEx: node["regex"],
		type: String(node["description"]).includes("(YYMMDD)")
			? "date"
			: "string",
	};
});

interface HIBCUDI_PI {
	expirationDate?: string;
	lotNumber?: string;
	serialNumber?: string;
}

export const ScannerComponent: React.FC<{
	jobId?: string;
	jobDocumentId?: string;
}> = ({ jobId, jobDocumentId }) => {
	const videoRef = useRef<HTMLVideoElement | null>(null);
	const canvasRef = useRef<HTMLCanvasElement | null>(null);
	const [scanning, setScanning] = useState(true);
	const [scanResult, setScanResult] = useState<ReturnedData[]>([]);
	const [controls, setControls] = useState<IScannerControls | null>(null);
	const [permissionState, setPermissionState] = useState<string>("prompt");
	const [isReady, setIsReady] = useState(false);
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [isDialogSuccess, setIsDialogSuccess] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [lotNumber, setLotNumber] = useState<string>("");
	const [formData, setFormData] = useState<{
		jobId?: string;
		jobDocumentId?: string;
	}>({
		jobId: jobId,
		jobDocumentId: jobDocumentId,
	});
	const scanInterval = useRef<NodeJS.Timeout | null>(null);
	const navigate = useNavigate();
	const roiFactor = 0.3; // ROI Größe (50% der Bildmitte)
	const isScanningRef = useRef(false);

	useEffect(() => {
		if (scanning && videoRef.current) {
			startScanning();
		} else if (!scanning && controls) {
			controls.stop();
		}
	}, [scanning]);

	useEffect(() => {
		const initScanner = async () => {
			const hasPermission = await checkCameraPermission();
			if (hasPermission || permissionState === "prompt") {
				setScanning(true);
			}
		};

		initScanner();
	}, []);

	useEffect(() => {
		if (scanResult.length > 0) {
			const lotNumber = scanResult.find(
				(item) => item.code === "BATCH/LOT"
			);
			if (lotNumber) {
				setLotNumber(lotNumber.value);
				setIsReady(true);
			}
		}
	}, [scanResult]);

	const parseHIBCUDI_Detailed = (code: string): ReturnedData[] | null => {
		if (!code.startsWith("+")) {
			Logger.warn("Kein HIBC UDI Code erkannt (fehlendes '+').");
			return null;
		}
		const withoutPlus = code.substring(1);
		const separator = withoutPlus.includes("/") ? "/" : "\u001D";
		const parts = withoutPlus.split(separator);
		if (parts.length === 0) return null;

		const di = parts[0];
		const pi: HIBCUDI_PI = {};

		parts.slice(1).forEach((part) => {
			if (part.startsWith("$")) {
				// Bei "$" wird die Lot-/Chargennummer erwartet (ohne "$")
				pi.lotNumber = part.substring(1);
			} else if (part.startsWith("16D")) {
				// "16D" gefolgt von 8 Ziffern (YYYYMMDD) – hier behandeln wir das als Datum
				const datePart = part.substring(3);
				if (/^\d{8}$/.test(datePart)) {
					const year = datePart.substring(0, 4);
					const month = datePart.substring(4, 6);
					const day = datePart.substring(6, 8);
					// Formatierung als DD.MM.YYYY
					pi.expirationDate = `${day}.${month}.${year}`;
				} else {
					pi.expirationDate = datePart;
				}
			} else if (part.startsWith("Q")) {
				// Bei "Q" nehmen wir an, es handelt sich um die Seriennummer
				pi.serialNumber = part;
			} else {
				// Andere Segmente können hier ergänzt werden
				Logger.log("Unbekanntes PI-Subfeld:", part);
			}
		});

		return [
			//{ code: "DI", description: "Geräte-ID", value: di, type: "string" },
			{
				code: "BATCH/LOT",
				description: "Chargen-/Lotnummer",
				value: pi.lotNumber || "",
				type: "string",
			},
			//{ code: "EXP", description: "Ablaufdatum", value: pi.expirationDate || "", type: "date" },
			//{ code: "SER", description: "Seriennummer", value: pi.serialNumber || "", type: "string" }
		];
	};

	const findMatchingAI = (
		code: string,
		pos: number
	): ApplicationIdentifier | null => {
		const sortedAIs = aiDefinitions
			.slice()
			.sort((a, b) => Number(b.ai) - Number(a.ai));
		for (const aiDef of sortedAIs) {
			if (code.startsWith(aiDef.ai, pos)) {
				Logger.log(
					`Found AI "${aiDef.ai}" (${aiDef.description}) at position ${pos}`
				);
				return aiDef;
			}
		}
		Logger.warn(
			`No matching AI found at position ${pos} in code "${code}"`
		);
		return null;
	};

	const convertToTimestamp = (dateStr: string): string => {
		if (!dateStr || dateStr.length !== 6) {
			return dateStr;
		}

		try {
			const year = parseInt(dateStr.substring(0, 2));
			const month = parseInt(dateStr.substring(2, 4)) - 1;
			const day = parseInt(dateStr.substring(4, 6));

			const fullYear = year < 50 ? 2000 + year : 1900 + year;

			const date = new Date(fullYear, month, day);
			return date.toLocaleDateString("de-DE", {
				year: "numeric",
				month: "2-digit",
				day: "2-digit",
			});
		} catch (e) {
			Logger.log("Failed to parse date:", dateStr, e);
			return dateStr;
		}
	};

	const parseGS1Raw = (text: string): ReturnedData[] => {
		const GS = "\u001D";
		const segments = text.split(GS);
		const result: ReturnedData[] = [];

		Logger.log(segments, "Segments");

		for (const seg of segments) {
			let pos = 0;
			while (pos < seg.length) {
				const aiDef = findMatchingAI(seg, pos);
				if (!aiDef) {
					Logger.warn(`Unknown AI at pos ${pos} in "${seg}"`);
					break;
				}
				pos += aiDef.ai.length;

				let value: string;
				if (aiDef.regEx) {
					// Use regex pattern to extract the data
					const remainingText = seg.substring(pos);
					const regExp = new RegExp(aiDef.regEx);
					const match = remainingText.match(regExp);

					if (match && match[1]) {
						value = match[1];
						pos += match[0].length;
					} else {
						// If regex doesn't match, take until end of segment as fallback
						value = remainingText;
						pos = seg.length;
					}
				} else {
					// Fallback for missing regex - get the rest of the segment
					value = seg.substring(pos);
					pos = seg.length;
				}

				if (aiDef.type === "date") {
					value = convertToTimestamp(value);
				}

				result.push({
					code: aiDef.code,
					description: aiDef.description,
					value,
					type: aiDef.type,
				});
			}
		}

		return result;
	};

	// Neue Funktion zum Parsen von QR-Codes (z.B. für Dentlab Auftragsetiketten)
	const parseQRCode = (text: string): boolean => {
		Logger.log("Parsing QR code:", text);
		if (!jobId && !jobDocumentId) {
			const jobIdMatch = text.match(/job\/(\d+)/);

			if (jobIdMatch && jobIdMatch[1]) {
				setIsDialogOpen(true);
				const jobId = jobIdMatch[1];
				Logger.log("Detected Dentlab job ID:", jobId);
				navigate(`/job/${jobId}`);

				if (controls) {
					controls.stop();
					setControls(null);
				}

				return true;
			}
		}

		Logger.log("QR code is not a dentlab job URL, ignoring");
		return false;
	};

	const [codeReader] = useState(() => {
		const hints = new Map();
		// Unterstützte Formate erweitert um QR_CODE
		hints.set(DecodeHintType.POSSIBLE_FORMATS, [
			BarcodeFormat.DATA_MATRIX,
			BarcodeFormat.QR_CODE,
		]);
		return new BrowserMultiFormatReader(hints);
	});

	const checkCameraPermission = async (): Promise<boolean> => {
		try {
			const devices = await navigator.mediaDevices.enumerateDevices();
			const cameras = devices.filter(
				(device) => device.kind === "videoinput"
			);

			if (cameras.length === 0) {
				Logger.warn("No camera devices found");
				return false;
			}

			if (navigator.permissions && navigator.permissions.query) {
				const result = await navigator.permissions.query({
					name: "camera" as PermissionName,
				});
				setPermissionState(result.state);

				result.addEventListener("change", () => {
					setPermissionState(result.state);
				});

				return result.state === "granted";
			} else {
				return true;
			}
		} catch (error) {
			Logger.log("Error checking camera permission:", error);
			return false;
		}
	};

	const startScanning = async () => {
		if (!videoRef.current || !canvasRef.current) return;

		// Prevent multiple scanning attempts
		if (isScanningRef.current) return;
		isScanningRef.current = true;

		// Clean up any existing resources first
		if (videoRef.current.srcObject) {
			const oldStream = videoRef.current.srcObject as MediaStream;
			oldStream.getTracks().forEach((track) => track.stop());
			videoRef.current.srcObject = null;
		}

		if (scanInterval.current) {
			clearInterval(scanInterval.current);
			scanInterval.current = null;
		}

		if (controls) {
			controls.stop();
			setControls(null);
		}

		const hasPermission = await checkCameraPermission();
		if (!hasPermission && permissionState !== "prompt") {
			alert(
				"Camera access is required for scanning. Please enable camera access in your browser settings."
			);
			isScanningRef.current = false;
			return;
		}

		try {
			// Video-Stream abrufen und starten
			const stream = await navigator.mediaDevices.getUserMedia({
				video: {
					facingMode: "environment",
					width: { ideal: 1920 },
					height: { ideal: 1080 },
				},
			});

			// Make sure component hasn't unmounted during getUserMedia call
			if (!videoRef.current) {
				stream.getTracks().forEach((track) => track.stop());
				isScanningRef.current = false;
				return;
			}

			videoRef.current.srcObject = stream;

			// Use event listener for play instead of awaiting it directly
			const playPromise = new Promise<void>((resolve, reject) => {
				if (!videoRef.current)
					return reject("Video element not available");

				const onPlaying = () => {
					videoRef.current?.removeEventListener("playing", onPlaying);
					resolve();
				};

				const onError = (err: any) => {
					videoRef.current?.removeEventListener("error", onError);
					reject(err);
				};

				videoRef.current.addEventListener("playing", onPlaying);
				videoRef.current.addEventListener("error", onError);

				// Start playing, but don't await it
				videoRef.current.play().catch((err) => {
					// If play fails, make sure to remove the listeners
					videoRef.current?.removeEventListener("playing", onPlaying);
					videoRef.current?.removeEventListener("error", onError);
					reject(err);
				});
			});

			// Wait for video to start playing
			await playPromise;

			// Get references after successful play
			const video = videoRef.current;
			const canvas = canvasRef.current;

			// Double check that refs are still valid
			if (!video || !canvas) {
				stream.getTracks().forEach((track) => track.stop());
				isScanningRef.current = false;
				return;
			}

			const ctx = canvas.getContext("2d", { willReadFrequently: true });

			if (!ctx) {
				throw new Error("Konnte keinen Canvas-Context erstellen");
			}

			setIsLoading(false);

			// Scan-Intervall mit ROI starten
			scanInterval.current = setInterval(async () => {
				if (!video || !canvas || !ctx) return;

				const vw = video.videoWidth;
				const vh = video.videoHeight;

				if (!vw || !vh) return;

				// ROI-Box berechnen
				const w = vw * roiFactor;
				const h = w;
				const x = (vw - w) / 2;
				const y = (vh - h) / 2;

				// Canvas auf ROI-Größe setzen
				canvas.width = w;
				canvas.height = h;

				// Nur die ROI in den Canvas zeichnen
				ctx.drawImage(video, x, y, w, h, 0, 0, w, h);

				try {
					// Barcode aus dem Canvas-Bereich dekodieren
					const result = await codeReader.decodeFromCanvas(canvas);

					if (result) {
						setScanning(false);
						const rawText = result.getText();
						const format = result.getBarcodeFormat();
						Logger.log(
							"Raw scanned text:",
							rawText,
							"Format:",
							format
						);

						// Stream und Intervall stoppen
						clearInterval(scanInterval.current as NodeJS.Timeout);
						scanInterval.current = null;
						stream.getTracks().forEach((track) => track.stop());

						// Bei QR-Codes nur Job-URLs verarbeiten, andere ignorieren
						if (format === BarcodeFormat.QR_CODE) {
							const handled = parseQRCode(rawText);
							if (handled) {
								isScanningRef.current = false;
								return;
							}
						}

						let parsed = null;

						if (rawText.startsWith("+")) {
							parsed = parseHIBCUDI_Detailed(rawText);
						} else {
							parsed = parseGS1Raw(rawText);
						}

						if (parsed) {
							setScanResult(parsed);
						}

						setIsLoading(true);
						isScanningRef.current = false;
						clearTimeout(timeoutId);
					}
				} catch (error) {
					// Kein Code in diesem Frame gefunden - ignorieren
				}
			}, 200); // Alle 200ms scannen

			// Timeout nach 10 Sekunden, falls kein Barcode gefunden wird
			const timeoutId = setTimeout(() => {
				if (scanInterval.current) {
					clearInterval(scanInterval.current);
					scanInterval.current = null;
				}
				if (stream) {
					stream.getTracks().forEach((track) => track.stop());
				}
				setScanning(false);
				setIsLoading(true);
				isScanningRef.current = false;
			}, 10000);

			return () => {
				clearTimeout(timeoutId);
				if (scanInterval.current) {
					clearInterval(scanInterval.current);
					scanInterval.current = null;
				}
				if (stream) {
					stream.getTracks().forEach((track) => track.stop());
				}
				isScanningRef.current = false;
			};
		} catch (error) {
			if (error instanceof DOMException && error.name === "AbortError") {
				showNotification({
					message: "Camera access was interrupted. Please try again.",
					type: "warning",
					duration: 3000,
				});
			} else {
				showNotification({
					message: (error as Error).message,
					type: "error",
					duration: 3000,
				});
				setScanning(false);
				setControls(null);
				setIsLoading(false);
				isScanningRef.current = false;

				if (
					error instanceof Error &&
					error.name === "NotAllowedError"
				) {
					alert(
						"Camera access was denied. You can enable it in your browser settings."
					);
				}
			}
		}
	};

	// Cleanup beim Komponenten-Unmount
	useEffect(() => {
		return () => {
			if (scanInterval.current) {
				clearInterval(scanInterval.current);
			}
			if (videoRef.current && videoRef.current.srcObject) {
				const stream = videoRef.current.srcObject as MediaStream;
				stream.getTracks().forEach((track) => track.stop());
			}
		};
	}, []);

	const onDialogClose = (success: boolean) => {
		setIsDialogOpen(false);
		setIsDialogSuccess(success);
	};

	return (
		<div className="flex flex-col items-center justify-center w-full h-full">
			{scanning ? (
				<>
					{isLoading && (
						<div className="absolute inset-0 flex items-center justify-center">
							<CircularProgress />
						</div>
					)}
					<div className="relative w-full h-full">
						<video
							ref={videoRef}
							className="absolute inset-0 max-h-full min-w-full object-cover"
							style={{ opacity: isLoading ? 0.5 : 1 }}
							autoPlay
							playsInline
							muted
						/>
						{/* ROI Overlay */}
						{!isLoading && (
							<div className="absolute inset-0 pointer-events-none">
								{/* Top overlay */}
								<div
									className="absolute top-0 left-0 right-0 bg-black/60"
									style={{
										height: `calc(50% - ${
											roiFactor * 50
										}vw)`,
									}}
								></div>

								{/* Bottom overlay */}
								<div
									className="absolute bottom-0 left-0 right-0 bg-black/60"
									style={{
										height: `calc(50% - ${
											roiFactor * 50
										}vw)`,
									}}
								></div>

								{/* Left overlay */}
								<div
									className="absolute left-0 bg-black/60"
									style={{
										top: `calc(50% - ${roiFactor * 50}vw)`,
										height: `${roiFactor * 100}vw`,
										width: `calc(50% - ${
											roiFactor * 50
										}vw)`,
									}}
								></div>

								{/* Right overlay */}
								<div
									className="absolute right-0 bg-black/60"
									style={{
										top: `calc(50% - ${roiFactor * 50}vw)`,
										height: `${roiFactor * 100}vw`,
										width: `calc(50% - ${
											roiFactor * 50
										}vw)`,
									}}
								></div>

								{/* ROI border */}
								<div className="absolute inset-0 flex items-center justify-center">
									<div
										style={{
											width: `${roiFactor * 100}vw`,
											height: `${roiFactor * 100}vw`,
											border: "3px solid rgba(255, 255, 255, 0.8)",
											boxSizing: "border-box",
											borderRadius: "2px",
										}}
									></div>
								</div>
							</div>
						)}
						{/* Unsichtbarer Canvas für die ROI */}
						<canvas ref={canvasRef} style={{ display: "none" }} />
						<div className="absolute bottom-6 left-0 right-0 flex justify-center">
							<button
								className="bg-[#003261] text-white py-2 px-6 rounded-full shadow-lg transition-colors active:bg-[#003261]/80"
								onClick={() => {
									setScanResult([]);
									setIsDialogSuccess(false);
									setIsDialogOpen(true);
									setScanning(false);
								}}
							>
								Daten manuell erfassen
							</button>
						</div>
					</div>
				</>
			) : (
				!isDialogSuccess &&
				(scanResult.length > 0 ? (
					<div className="p-4 w-full">
						<strong className="text-lg font-medium block mb-2">
							Erfasste Daten:
						</strong>
						<ul className="divide-y">
							{scanResult.map((item, index) => (
								<li key={index} className="py-2">
									<span className="font-medium">
										{item.description.replace(
											" (YYMMDD)",
											""
										)}
										:
									</span>{" "}
									{item.value}
								</li>
							))}
						</ul>
						<button
							onClick={() => {
								setScanResult([]);
								setIsDialogSuccess(false);
								setIsDialogOpen(false);
								setScanning(true);
								setLotNumber("");
							}}
							className="mt-4 w-full bg-[#003261] transition-colors active:bg-[#003261]/80 text-white py-2 px-4 rounded-md"
						>
							Erneut scannen
						</button>
						{isReady ? (
							<button
								onClick={() => setIsDialogOpen(true)}
								className="mt-4 w-full bg-[#003261] transition-colors active:bg-[#003261]/80 text-white py-2 px-4 rounded-md"
							>
								Zu Dokument hinzufügen
							</button>
						) : (
							<button
								onClick={() => {
									setIsDialogOpen(true);
								}}
								className="mt-4 w-full bg-[#003261] transition-colors active:bg-[#003261]/80 text-white py-2 px-4 rounded-md"
							>
								Daten manuell erfassen
							</button>
						)}
					</div>
				) : (
					<div className="p-4 w-full flex flex-col items-center justify-center">
						<strong className="text-lg font-medium block mb-2">
							Keine Daten erfasst
						</strong>
						<button
							onClick={() => {
								setScanResult([]);
								setIsDialogSuccess(false);
								setIsDialogOpen(false);
								setScanning(true);
							}}
							className="mt-4 w-full bg-[#003261] transition-colors active:bg-[#003261]/80 text-white py-2 px-4 rounded-md"
						>
							Erneut versuchen
						</button>
						<button
							onClick={() => {
								setIsDialogOpen(true);
							}}
							className="mt-4 w-full bg-[#003261] transition-colors active:bg-[#003261]/80 text-white py-2 px-4 rounded-md"
						>
							Daten manuell erfassen
						</button>
					</div>
				))
			)}
			<AttachMaterialDialog
				jobId={formData.jobId}
				jobDocumentId={formData.jobDocumentId}
				open={isDialogOpen}
				lotNumber={lotNumber}
				onClose={onDialogClose}
			/>
		</div>
	);
};
